import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, NavLink, useNavigate, useParams } from "react-router-dom";
import { User, Layers, Coins, Users, Package, LogOut, Shield, MapPin, Cake, CalendarDays, Pencil } from "lucide-react";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Avatar, LevelBar, CardTile } from "../components/community/Bits";
import { CollectionManager, SetTiles, useCollection, ViewAll } from "../components/community/Collection";
import { PointsPanel } from "../components/community/Points";
import { LangBadge } from "../components/shop/Flag";
import { SecurityPanel, VerifyBanner } from "../components/security/SecurityPanel";

const Sidebar = ({ onLogout }) => {
  const { tr } = useLang();
  const links = [["", User, tr("Mein Profil", "My profile")], ["collection", Layers, tr("Meine Sammlung", "My collection")], ["points", Coins, tr("Jolgen Punkte", "Jolgen Points")], ["orders", Package, tr("Bestellungen", "Orders")], ["security", Shield, tr("Sicherheit", "Security")]];
  const cls = ({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition-colors ${isActive ? "bg-[#6E3FB8] text-white" : "text-[#120E1F] hover:bg-[#8A5CD6]/10"}`;
  return (
    <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:sticky lg:top-36" data-testid="member-sidebar">
      {links.map(([to, I, l]) => <NavLink key={l} end to={`/member${to ? `/${to}` : ""}`} className={cls} data-testid={`member-nav-${to || "profile"}`}><I className="h-4 w-4" />{l}</NavLink>)}
      <NavLink to="/community" className={cls} data-testid="member-nav-community"><Users className="h-4 w-4" />Community</NavLink>
      <button onClick={onLogout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-[#707783] hover:text-red-600 transition-colors" data-testid="member-logout-btn"><LogOut className="h-4 w-4" />{tr("Abmelden", "Sign out")}</button>
    </nav>
  );
};

const ProfileOverview = ({ me, cards }) => {
  const { tr, lang } = useLang();
  return (
    <div className="space-y-6" data-testid="member-profile">
      <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-6 flex flex-col sm:flex-row gap-6">
        <Avatar id={me.avatar} size="h-28 w-28" />
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-3"><h1 className="font-display text-4xl text-[#120E1F]" data-testid="member-display-name">{me.display_name}</h1>
            <span className="badge-pill bg-[#8A5CD6]/15 text-[#6E3FB8]">Jolgen Member</span>
            <Link to="/member/edit" className="ml-auto btn-outline" data-testid="edit-profile-btn"><Pencil className="h-3.5 w-3.5" />{tr("Bearbeiten", "Edit")}</Link></div>
          {me.about && <p className="text-sm text-[#707783]">{me.about}</p>}
          <div className="flex flex-wrap gap-4 text-xs text-[#707783]">
            {me.country && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{me.country}</span>}
            {me.birthday && <span className="flex items-center gap-1"><Cake className="h-3.5 w-3.5" />{new Date(me.birthday).toLocaleDateString(lang === "en" ? "en-GB" : "de-DE")}</span>}
            <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{tr("Mitglied seit", "Member since")} {new Date(me.member_since).toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { month: "long", year: "numeric" })}</span>
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-[#120E1F] p-5 space-y-3" data-testid="member-level-card"><p className="font-display text-3xl text-white">Level {me.level}</p><p className="text-xs text-[#C9B6F2]">{me.level_name}</p><LevelBar m={me} dark /></div>
        <div className="rounded-2xl bg-gradient-to-br from-[#6E3FB8] to-[#8A5CD6] p-5 space-y-3" data-testid="member-points-card"><p className="text-xs text-white/70">Jolgen {tr("Punkte", "Points")}</p><p className="font-mono text-4xl text-white">{me.points.toLocaleString("de-DE")}</p>
          <Link to="/member/points" className="inline-block rounded-full bg-white/15 px-4 py-1.5 text-xs text-white hover:bg-white/25 transition-colors" data-testid="view-rewards-btn">{tr("Prämien ansehen", "View rewards")}</Link></div>
      </div>
      <div className="space-y-3"><div className="flex justify-between items-end"><h2 className="font-display text-2xl">{tr("Sammlungs-Highlights", "Collection highlights")}</h2><ViewAll to="/member/collection" label={tr("Alle", "View all")} /></div>
        {cards.length === 0 ? <p className="text-sm text-[#707783]">{tr("Füge deine ersten Karten hinzu.", "Add your first cards.")} <Link to="/member/collection" className="text-[#6E3FB8] underline">{tr("Jetzt starten", "Start now")}</Link></p>
          : <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">{cards.slice(0, 6).map((c) => <CardTile key={c.card_id} c={c} />)}</div>}</div>
      {cards.length > 0 && <div className="space-y-3"><h2 className="font-display text-2xl">{tr("Meine Lieblings-Sets", "My favourite sets")}</h2><SetTiles cards={cards} /></div>}
    </div>
  );
};

const Orders = () => {
  const { tr, t, lang } = useLang();
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/account/orders").then((r) => setOrders(r.data)); }, []);
  return (
    <div className="space-y-4" data-testid="orders-list">
      <h2 className="font-display text-3xl">{tr("Bestellungen", "Orders")}</h2>
      {orders && orders.length === 0 && <p className="text-[#707783]" data-testid="no-orders">{t("no_orders")}</p>}
      {(orders || []).map((o) => (
        <div key={o.id} className="bg-white rounded-2xl border border-[#120E1F]/5 p-5" data-testid={`order-${o.order_number}`}>
          <div className="flex flex-wrap justify-between gap-3"><div><p className="font-mono text-sm">{o.order_number}</p><p className="text-xs text-[#707783]">{new Date(o.created_at).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB")}</p></div>
            <span className="badge-pill bg-[#F2C230]/15 text-[#6E3FB8] h-fit">{t("st")[o.status]}</span><p className="font-mono font-semibold">{eur(o.total)}</p></div>
          <ul className="text-sm text-[#707783] mt-3 space-y-1">{o.items.map((i) => <li key={i.product_id} className="flex flex-wrap items-center gap-2">{i.quantity}× {lang === "en" ? i.name_en : i.name}<LangBadge code={i.language} testid={`order-item-lang-${i.sku || i.product_id}`} /></li>)}</ul>
          {o.tracking_number && <p className="text-xs mt-2">{t("tracking")}: <span className="font-mono">{o.tracking_number}</span></p>}
        </div>
      ))}
    </div>
  );
};

export default function Member() {
  const { user, logout } = useAuth();
  const { tab } = useParams();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [cards, reloadCards] = useCollection(Boolean(user && user.onboarded));
  const reload = useCallback(() => api.get("/community/me").then((r) => setMe(r.data)).catch(() => setMe(null)), []);
  useEffect(() => { if (user && user.onboarded) reload(); }, [user, reload]);

  if (user === null) return <div className="min-h-[60vh]" />;
  if (!user) return <Navigate to="/login" />;
  if (user.role === "admin") return <Navigate to="/admin" />;
  if (!user.onboarded) return <Navigate to="/join" />;
  if (!me) return <div className="min-h-[60vh]" />;
  if (tab === "community") return <Navigate to="/community" replace />;
  if (tab && !["collection", "points", "orders", "security"].includes(tab)) return <Navigate to="/member" replace />;
  const reloadAll = () => { reloadCards(); reload(); };

  return (
    <div className="bg-[#F7F5FB] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-[220px_1fr] gap-8">
        <Sidebar onLogout={async () => { await logout(); nav("/"); }} />
        <div className="space-y-6">
          <VerifyBanner />
          {!tab && <ProfileOverview me={me} cards={cards} />}
          {tab === "collection" && <CollectionManager cards={cards} reload={reloadAll} />}
          {tab === "points" && <PointsPanel me={me} reload={reload} />}
          {tab === "orders" && <Orders />}
          {tab === "security" && <SecurityPanel />}
        </div>
      </div>
    </div>
  );
}
